import { useCallback, useEffect, useState } from "react";

import {
  buildOverviewUrl,
  buildWorkspaceSelectionUrl,
  readActiveView,
  type WorkspaceActiveView,
  type WorkspaceSelectionLocation,
} from "./workspaceSelection.url";

export type { WorkspaceActiveView };

export interface WorkspaceSelectionBrowser {
  location: WorkspaceSelectionLocation;
  history: Pick<History, "pushState" | "replaceState">;
  addEventListener: (
    type: "popstate",
    listener: EventListenerOrEventListenerObject,
  ) => void;
  removeEventListener: (
    type: "popstate",
    listener: EventListenerOrEventListenerObject,
  ) => void;
}

export interface WorkspaceSelectionController {
  activeView: WorkspaceActiveView;
  selectOverview: () => void;
  selectWorkspace: (workspaceId: string) => void;
}

const isViewEqual = (
  a: WorkspaceActiveView,
  b: WorkspaceActiveView,
): boolean => {
  if (a.type !== b.type) {
    return false;
  }
  if (a.type === "workspace" && b.type === "workspace") {
    return a.id === b.id;
  }
  return true;
};

const buildViewUrl = (
  location: WorkspaceSelectionLocation,
  view: WorkspaceActiveView,
): string =>
  view.type === "overview"
    ? buildOverviewUrl(location)
    : buildWorkspaceSelectionUrl(location, view.id);

const isUrlCanonical = (search: string, view: WorkspaceActiveView): boolean => {
  const params = new URLSearchParams(search);
  if (view.type === "overview") {
    return params.get("view") === "overview" && !params.has("workspace");
  }
  return params.get("workspace") === view.id && !params.has("view");
};

export const useWorkspaceSelectionController = (
  workspaceIds: ReadonlyArray<string>,
  browser: WorkspaceSelectionBrowser = window,
): WorkspaceSelectionController => {
  const [activeView, setActiveView] = useState<WorkspaceActiveView>(() =>
    readActiveView(browser.location.search, workspaceIds),
  );

  const replaceView = useCallback(
    (view: WorkspaceActiveView) => {
      browser.history.replaceState(
        null,
        "",
        buildViewUrl(browser.location, view),
      );
    },
    [browser],
  );

  // Canonicalize URL and fall back to overview when workspace disappears.
  useEffect(() => {
    let resolved = activeView;
    if (
      activeView.type === "workspace" &&
      !workspaceIds.includes(activeView.id)
    ) {
      resolved = { type: "overview" };
    }
    if (!isViewEqual(resolved, activeView)) {
      setActiveView(resolved);
    }
    if (!isUrlCanonical(browser.location.search, resolved)) {
      replaceView(resolved);
    }
  }, [activeView, browser.location.search, replaceView, workspaceIds]);

  useEffect(() => {
    const onPopState = () => {
      const resolved = readActiveView(browser.location.search, workspaceIds);
      if (!isUrlCanonical(browser.location.search, resolved)) {
        replaceView(resolved);
      }
      setActiveView(resolved);
    };
    browser.addEventListener("popstate", onPopState);
    return () => browser.removeEventListener("popstate", onPopState);
  }, [browser, replaceView, workspaceIds]);

  const selectOverview = useCallback(() => {
    if (activeView.type === "overview") {
      return;
    }
    browser.history.pushState(null, "", buildOverviewUrl(browser.location));
    setActiveView({ type: "overview" });
  }, [activeView, browser]);

  const selectWorkspace = useCallback(
    (workspaceId: string) => {
      if (activeView.type === "workspace" && activeView.id === workspaceId) {
        return;
      }
      browser.history.pushState(
        null,
        "",
        buildWorkspaceSelectionUrl(browser.location, workspaceId),
      );
      setActiveView({ type: "workspace", id: workspaceId });
    },
    [activeView, browser],
  );

  return { activeView, selectOverview, selectWorkspace };
};
